import { useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { api } from '../../shared/api/client';
import type { ClassroomDetail } from '../../shared/api/types';
import { Button, ErrorState, Loading, Logo } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { shortDate } from './shared';

const LETTERS = 'abcdefghij';

/**
 * Hoja del cuestionario para imprimir o guardar como PDF, sin menús.
 * «Para alumnos»: sin respuestas, con nombre y calificación arriba. «Con respuestas» (?respuestas=1): solo quien enseña.
 */
export function QuizPrintPage() {
  const { classroomId = '', quizId = '' } = useParams();
  const [params] = useSearchParams();
  const me = useMe();
  const churchId = me.activeChurchId;
  const data = useAsync(
    (signal) => (churchId ? api.get<ClassroomDetail>(`/organization/churches/${churchId}/classrooms/${classroomId}`, signal) : Promise.resolve(null)),
    [churchId, classroomId],
  );
  const room = data.status === 'ready' ? data.data : null;
  const quiz = room?.quizzes.find((q) => q.id === quizId) ?? null;
  const answers = params.get('respuestas') === '1' && Boolean(room?.canTeach);
  const session = quiz?.sessionId ? room?.sessions.find((s) => s.id === quiz.sessionId) : undefined;
  const ready = quiz !== null;

  useEffect(() => {
    if (ready && typeof window.print === 'function' && !navigator.webdriver) {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [ready]);

  const total = quiz?.questions.reduce((n, q) => n + q.points, 0) ?? 0;
  return (
    <div className="print-sheet">
      <div className="print-sheet__bar no-print">
        <Link className="btn btn--ghost" to={quiz?.sessionId ? `/app/salones/${classroomId}/clases/${quiz.sessionId}` : `/app/salones/${classroomId}?ver=temario`}>
          ← Volver a la clase
        </Link>
        <Button onClick={() => window.print()}>Imprimir o guardar PDF</Button>
        <span className="small muted">En el cuadro de impresión elige «Guardar como PDF».</span>
      </div>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} /> : null}
      {data.status === 'ready' && !quiz ? <p>No encontré este cuestionario.</p> : null}
      {quiz && room ? (
        <>
          <header className="print-sheet__head">
            <Logo />
            <div>
              <h1>
                {quiz.title}
                {answers ? ' · Con respuestas' : ''}
              </h1>
              <p>
                {me.activeContext?.churchName} · {room.name}
                {session ? ` · ${session.topic?.title ?? 'Clase'} (${shortDate(session.startsAt)})` : ''}
              </p>
            </div>
          </header>
          {!answers ? (
            <div className="quiz-print__fields">
              <span>Nombre: ______________________________</span>
              <span>Fecha: ____________</span>
              <span>Calificación: ______ / {total}</span>
            </div>
          ) : null}
          {quiz.instructions ? <p className="pre-line">{quiz.instructions}</p> : null}
          <ol className="quiz-print">
            {quiz.questions.map((q) => (
              <li key={q.id}>
                <p className="quiz-print__q">
                  {q.text}
                  {q.points !== 1 ? <span className="muted"> ({q.points} puntos)</span> : null}
                </p>
                {q.type === 'choice' ? (
                  <ul className="quiz-print__options">
                    {q.options.map((o, i) => (
                      <li key={i} className={answers && q.correct === i ? 'quiz-print__correct' : undefined}>
                        {answers && q.correct === i ? '✔' : '○'} {LETTERS[i]}) {o}
                      </li>
                    ))}
                  </ul>
                ) : answers ? (
                  <p className="small muted">Respuesta abierta: califícala según la lección.</p>
                ) : (
                  <div className="quiz-print__lines" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </div>
                )}
              </li>
            ))}
          </ol>
        </>
      ) : null}
    </div>
  );
}
